<template>
  <v-modal id="pax-update-modal" class-name="modal-fullscreen-sm-down" size="lg" @closed="$emit('closed')">
    <modal-header>
      <modal-title>
        {{ $t("clientTour.updatePax") }}
      </modal-title>
    </modal-header>

    <modal-body>
      <form id="case-client-update-form" class="d-flex flex-column justify-content-between h-100">
        <!-- Basic Information -->
        <fieldset class="mb-16">
          <legend class="visually-hidden">{{ $t("client.basicInformation") }}</legend>

          <div class="row">
            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-name"
                v-model="name"
                :label="`${$t('general.shared.name')}*`"
                :yup-errors-variable="errors.name"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-email"
                v-model="email"
                :label="`${$t('general.shared.email')}*`"
                :yup-errors-variable="errors.email"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
                type="email"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-phone"
                v-model="phone"
                :label="$t('general.shared.phone')"
                :yup-errors-variable="errors.phone"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-surname-1"
                v-model="surname1"
                :label="$t('general.shared.surname')"
                :yup-errors-variable="errors.surname1"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-surname-2"
                v-model="surname2"
                :label="$t('general.shared.secondSurname')"
                :yup-errors-variable="errors.surname2"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form
                id="case-pax-update-document-type"
                :label="$t('general.shared.documentType')"
                :yup-errors-variable="errors.documentType"
                form-type="outline"
                class="mb-8"
              >
                <v-select
                  id="case-pax-update-document-type-select"
                  v-model="documentType"
                  :class="{ 'is-invalid': !!errors.documentType }"
                  :options="documentTypes"
                  :reduce="(o: any) => o.key"
                  autocomplete="off"
                >
                  <template v-slot:no-options>
                    <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
                  </template>
                  <template v-slot:option="option">
                    <span class="text-uppercase">{{ option.label }}</span>
                  </template>
                  <template v-slot:selected-option="option">
                    <span class="text-uppercase">{{ option.label }}</span>
                  </template>
                </v-select>
              </v-form>
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-document-number"
                v-model="documentNumber"
                :label="$t('general.shared.documentNumber')"
                :yup-errors-variable="errors.documentNumber"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>
          </div>
        </fieldset>

        <!-- Address Information -->
        <fieldset class="mb-16">
          <legend class="visually-hidden">{{ $t("general.shared.addressStreet") }}</legend>

          <div class="row">
            <div class="col-12">
              <v-form-input
                id="case-pax-update-address-street"
                v-model="addressStreet"
                :label="$t('general.shared.addressStreet')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-address-city"
                v-model="addressCity"
                :label="$t('general.shared.addressCity')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-address-cp"
                v-model="addressCp"
                :label="$t('general.shared.addressPostalCode')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-address-state"
                v-model="addressState"
                :label="$t('general.shared.addressState')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-address-country"
                v-model="addressCountry"
                :label="$t('general.shared.addressCountry')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>
          </div>
        </fieldset>

        <!-- Personal Information -->
        <fieldset class="mb-16">
          <legend class="visually-hidden">{{ $t('general.shared.birthDate') }}</legend>

          <div class="row">
            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-birth-date"
                v-model="birthDate"
                :label="$t('general.shared.birthDate')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
                type="date"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-language"
                v-model="language"
                :label="$t('general.shared.language')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-allergies"
                v-model="allergies"
                :label="$t('general.shared.allergies')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-diet"
                v-model="diet"
                :label="$t('general.shared.diet')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-height"
                v-model="height"
                :label="$t('general.shared.height')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>

            <div class="col-12 col-md-6">
              <v-form-input
                id="case-pax-update-weight"
                v-model="weight"
                :label="$t('general.shared.weight')"
                autocomplete="off"
                class="mb-8"
                form-type="outline"
              />
            </div>
          </div>
        </fieldset>

        <!-- Case-specific fields (only shown when caseTravellerId is provided) -->
        <template v-if="caseTravellerId">
          <!-- Arrival Information -->
          <fieldset class="mb-16">
            <legend class="separator-container">
              <span class="separator">
                <span class="separator-text text-sm">{{ $t('traveler.arrival') }}</span>
              </span>
            </legend>

            <div class="row">
              <div class="col-6">
                <v-form
                  id="case-pax-update-arrival-type"
                  :label="$t('traveler.arrivalType')"
                  form-type="outline"
                  class="mb-8"
                >
                  <v-select
                    v-model="arrivalType"
                    :options="arrivalTypeOptions"
                    :reduce="(o: any) => o.key"
                    autocomplete="off"
                  >
                    <template v-slot:no-options>
                      <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
                    </template>
                  </v-select>
                </v-form>
              </div>

              <div class="col-6">
                <v-form-input
                  id="case-pax-update-arrival-number"
                  v-model="arrivalNumber"
                  :label="$t('traveler.arrivalNumber')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                />
              </div>

              <div class="col-12 col-md-6">
                <v-form-input
                  id="case-pax-update-arrival-location"
                  v-model="arrivalLocation"
                  :label="$t('traveler.arrivalLocation')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                />
              </div>

              <div class="col-12 col-md-3">
                <v-form-input
                  id="case-pax-update-arrival-date"
                  v-model="arrivalDate"
                  :label="$t('traveler.arrivalDate')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                  type="date"
                />
              </div>

              <div class="col-12 col-md-3">
                <v-form-input
                  id="case-pax-update-arrival-hour"
                  v-model="arrivalHour"
                  :label="$t('traveler.arrivalHour')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                  type="time"
                />
              </div>
            </div>
          </fieldset>

          <!-- Departure Information -->
          <fieldset class="mb-16">
            <legend class="separator-container">
              <span class="separator">
                <span class="separator-text text-sm">{{ $t('traveler.departure') }}</span>
              </span>
            </legend>

            <div class="row">
              <div class="col-6">
                <v-form
                  id="case-pax-update-departure-type"
                  :label="$t('traveler.departureType')"
                  form-type="outline"
                  class="mb-8"
                >
                  <v-select
                    v-model="departureType"
                    :options="arrivalTypeOptions"
                    :reduce="(o: any) => o.key"
                    autocomplete="off"
                  >
                    <template v-slot:no-options>
                      <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
                    </template>
                  </v-select>
                </v-form>
              </div>

              <div class="col-6">
                <v-form-input
                  id="case-pax-update-departure-number"
                  v-model="departureNumber"
                  :label="$t('traveler.departureNumber')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                />
              </div>

              <div class="col-12 col-md-6">
                <v-form-input
                  id="case-pax-update-departure-location"
                  v-model="departureLocation"
                  :label="$t('traveler.departureLocation')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                />
              </div>

              <div class="col-12 col-md-3">
                <v-form-input
                  id="case-pax-update-departure-date"
                  v-model="departureDate"
                  :label="$t('traveler.departureDate')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                  type="date"
                />
              </div>

              <div class="col-12 col-md-3">
                <v-form-input
                  id="case-pax-update-departure-hour"
                  v-model="departureHour"
                  :label="$t('traveler.departureHour')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                  type="time"
                />
              </div>
            </div>
          </fieldset>

          <!-- Bike & Observations -->
          <fieldset class="mb-16">
            <legend class="separator-container">
              <span class="separator">
                <span class="separator-text text-sm">{{ $t('traveler.moreInformation') }}</span>
              </span>
            </legend>

            <div class="row">
              <div class="col-12 col-md-6">
                <v-form-input
                  id="case-pax-update-bike-type"
                  v-model="bikeType"
                  :label="$t('traveler.bikeType')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                />
              </div>

              <div class="col-12 col-md-6">
                <v-form-input
                  id="case-pax-update-room-type"
                  v-model="roomType"
                  :label="$t('tourBooking.roomType')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                />
              </div>

              <div class="col-12">
                <v-form-input
                  id="case-pax-update-observations"
                  v-model="observations"
                  :label="$t('emailTemplate.observations')"
                  autocomplete="off"
                  class="mb-8"
                  form-type="outline"
                />
              </div>
            </div>
          </fieldset>
        </template>

        <div class="d-flex justify-content-end gap-12 w-100">
          <v-button
            class="btn-icon w-fit"
            size="sm"
            variant="tertiary"
            @click="confirmDeletePax"
          >
            <v-icon icon="trash" size="sm" class="text-danger me-8" />
            {{ $t("general.button.remove") }}
          </v-button>

          <v-button
            :disabled="!meta.valid || accepted"
            :is-loading="accepted"
            class="btn-submit-form btn-icon w-fit"
            size="sm"
            variant="primary"
            @click="updatePax"
          >
            {{ $t("general.button.save") }}
            <v-icon icon="arrow-right" size="sm" space="ms-12" />
          </v-button>
        </div>
      </form>
    </modal-body>
  </v-modal>
</template>

<script lang="ts" setup name="ThePaxInfoEditModal">
import { computed, getCurrentInstance, nextTick, ref, watch } from "vue";
import { useField } from "vee-validate";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { VForm, VFormInput } from "@uniqoders/form";
import vSelect from "vue-select";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import useFormValidation from "@/helpers/form";
import useConcepts from "@/helpers/Concepts";
import { mapConcepts } from "@/helpers/ConceptHelper";
import api from "@/api";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useToast } from "vue-toastification";

const props = defineProps<{
  agency: string;
  caseId?: number | null;
  caseTravellerId?: number | null;
  pax: any;
}>();

const emit = defineEmits(["closed", "reloadPax"]);

const { t } = useI18n();
const toast = useToast();
const { concepts } = useConcepts();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;

const documentTypes = computed(() => {
  if (concepts.value && concepts.value["pax.document_type"]) {
    return mapConcepts(concepts.value["pax.document_type"]);
  }
  return [];
});

const arrivalTypeOptions = computed(() => {
  if (concepts.value && concepts.value["pax.arrival_type"]) {
    return mapConcepts(concepts.value["pax.arrival_type"]);
  }
  return [];
});

const rules = yup.object({
  name: yup.string().required().label(t("general.shared.name")),
  email: yup.string().email().required().label(t("general.shared.email")),
  phone: yup.string().nullable().label(t("general.shared.phone")),
  surname1: yup.string().nullable().label(t("general.shared.surname")),
  surname2: yup.string().nullable().label(t("general.shared.secondSurname")),
  documentType: yup.string().nullable().label(t("general.shared.documentType")),
  documentNumber: yup.string().nullable().label(t("general.shared.documentNumber")),
});

const { errors, meta, accepted } = useFormValidation(rules);

const { value: name } = useField<string>("name");
const { value: email } = useField<string>("email");
const { value: phone } = useField<string>("phone");
const { value: surname1 } = useField<string>("surname1");
const { value: surname2 } = useField<string>("surname2");
const { value: documentType } = useField<string>("documentType");
const { value: documentNumber } = useField<string>("documentNumber");

// Non-validated fields (optional meta attributes)
const addressStreet = ref("");
const addressCity = ref("");
const addressCp = ref("");
const addressState = ref("");
const addressCountry = ref("");
const birthDate = ref("");
const language = ref("");
const allergies = ref("");
const diet = ref("");
const height = ref("");
const weight = ref("");

// Case-specific fields (CaseTraveller meta)
const bikeType = ref("");
const roomType = ref("");
const observations = ref("");
const arrivalType = ref(null);
const arrivalNumber = ref("");
const arrivalDate = ref("");
const arrivalHour = ref("");
const arrivalLocation = ref("");
const departureType = ref(null);
const departureNumber = ref("");
const departureDate = ref("");
const departureHour = ref("");
const departureLocation = ref("");

watch(
  () => props.pax,
  async (newPax) => {
    if (newPax) {
      name.value = newPax?.name || "";
      email.value = newPax?.email || "";
      phone.value = newPax?.phone || "";
      surname1.value = newPax?.surname_1 || newPax?.surname1 || "";
      surname2.value = newPax?.surname_2 || newPax?.surname2 || "";
      documentType.value = newPax?.document_type || newPax?.documentType || null;
      documentNumber.value = newPax?.document_number || newPax?.documentNumber || "";

      addressStreet.value = newPax?.address_street || "";
      addressCity.value = newPax?.address_city || "";
      addressCp.value = newPax?.address_cp || "";
      addressState.value = newPax?.address_state || "";
      addressCountry.value = newPax?.address_country || "";
      birthDate.value = newPax?.birth_date || "";
      language.value = newPax?.language || "";
      allergies.value = newPax?.allergies || "";
      diet.value = newPax?.diet || "";
      height.value = newPax?.height || "";
      weight.value = newPax?.weight || "";

      // Case-specific fields (from CaseTraveller meta — present when opened from case setup)
      bikeType.value = newPax?.bike_type || "";
      roomType.value = newPax?.room_type || "";
      observations.value = newPax?.observations || "";
      arrivalType.value = newPax?.arrival_type || null;
      arrivalNumber.value = newPax?.arrival_number || "";
      arrivalDate.value = newPax?.arrival_date || "";
      arrivalHour.value = newPax?.arrival_hour || "";
      arrivalLocation.value = newPax?.arrival_location || "";
      departureType.value = newPax?.departure_type || null;
      departureNumber.value = newPax?.departure_number || "";
      departureDate.value = newPax?.departure_date || "";
      departureHour.value = newPax?.departure_hour || "";
      departureLocation.value = newPax?.departure_location || "";

      await nextTick();
    }
  },
  { immediate: true }
);

const updatePax = async () => {
  const payload: Record<string, any> = {
    name: name.value,
    email: email.value,
    phone: phone.value,
    surname_1: surname1.value,
    surname_2: surname2.value,
    document_type: documentType.value,
    document_number: documentNumber.value,
    address_street: addressStreet.value,
    address_city: addressCity.value,
    address_cp: addressCp.value,
    address_state: addressState.value,
    address_country: addressCountry.value,
    birth_date: birthDate.value,
    language: language.value,
    allergies: allergies.value,
    diet: diet.value,
    height: height.value,
    weight: weight.value,
  };

  // Include case-specific fields only when a case_traveller_id is provided
  if (props.caseTravellerId) {
    payload.case_traveller_id = props.caseTravellerId;
    payload.bike_type = bikeType.value;
    payload.room_type = roomType.value;
    payload.observations = observations.value;
    payload.arrival_type = arrivalType.value;
    payload.arrival_number = arrivalNumber.value;
    payload.arrival_date = arrivalDate.value;
    payload.arrival_hour = arrivalHour.value;
    payload.arrival_location = arrivalLocation.value;
    payload.departure_type = departureType.value;
    payload.departure_number = departureNumber.value;
    payload.departure_date = departureDate.value;
    payload.departure_hour = departureHour.value;
    payload.departure_location = departureLocation.value;
  }

  try {
    await api.case.updatePax(props.agency, props.pax?.id, payload);
    toast.success(t("general.shared.savedChanges"));
    emit("reloadPax");
    emit("closed");
  } catch (error: any) {
    console.log("error: ", error);
    toast.error(error?.response?.data?.message);
  }
};

const paxName = computed(() => {
  const name = props.pax?.name || '';
  const surname1 = props.pax?.surname1 || props.pax?.surname || '';
  const surname2 = props.pax?.surname2 || '';
  return `${name} ${surname1} ${surname2}`.trim();
});

const confirmDeletePax = () => {
  if (!$modal) {
    return;
  }

  $modal.delete({
    title: t('clientTour.traveler.removeTitle'),
    text: t('clientTour.traveler.removeText', { name: paxName.value }),
    deleteButtonText: t('general.button.remove'),
    deleteButtonLoadingText: t('general.button.removing'),
    rightButtonClasses: 'btn-tertiary',
    deleteButtonCallback: doDeletePax,
  });
};

const doDeletePax = async () => {
  try {
    await api.case.caseRemovePax(props.agency, Number(props.caseId), Number(props.pax?.id));
    toast.success(t('clientTour.traveler.removeTitle'));
    emit('reloadPax');
    emit('closed');
  } catch (error: any) {
    console.log('error: ', error);
    toast.error(error?.response?.data?.message || t('general.shared.somethingWentWrong'));
  }
};
</script>
